import BrowserConnection from '@/components/ChatInput/BrowserConnection';
import SpinningBorder from './SpinningBorder';
import React, { useState, useRef, useMemo, useCallback } from 'react';
import { StyleSheet, Pressable, Platform, View, Keyboard } from 'react-native';
import TextInput from '@/components/Typography/TextInput';
import { getComposerContentHeight } from './textareaLayout';
import { macComposerPasteProps } from '@/lib/macComposerPaste';
import * as Device from 'expo-device';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import ChatInputModelsButton from '@/components/ChatInput/ChatInputModelsButton';
import { getShadowThemeStyles, isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useUIStore, useAuthStore } from '@/store';
import VoiceModeButton from '@/components/ChatInput/VoiceModeButton';
import DictateModeButton from '@/components/ChatInput/DictateModeButton';
import DictateRecordingOverlay from '@/components/ChatInput/DictateRecordingOverlay';
import useDictateState from '@/hooks/useDictateState';
import dictateModeService, { DICTATE_STATES } from '@/lib/dictateMode';
import FastModeButton from '@/components/ChatInput/FastModeButton';
import WorkComposerActions from '@/components/ChatInput/WorkComposerActions';
import WorkThinkingPowerButton from '@/components/ChatInput/WorkThinkingPowerButton';
import { MAX_MODELS_SELECTABLE } from '@/constants/config';
import { isComposerEnterEvent, shouldComposerSendOnEnter } from '@/constants/utils/composerEnter';
import { getIOSSafariBubbleBarPadding } from '@/constants/utils/iosSafari';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';

const createStyles = (colors, theme, themeStyles, inputHeight, isDisabled, isXS, bubbleBarPadding = 0, isWorkMode = false) => StyleSheet.create({
  container: {
    width: '100%',
    marginBottom: bubbleBarPadding,
    position: 'relative',
  },
  inner: {
    flexDirection: 'row',
    borderRadius: 24,
    overflow: 'visible',
    borderWidth: 1,
    borderColor: themeStyles.borderColor,
    backgroundColor: Platform.OS === 'macos' ? colors.solid_color_block : themeStyles.backgroundColor,
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(18px) saturate(1.8)',
      WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
      boxShadow: isDarkTheme(theme) ? themeStyles.boxShadow : 'none',
    } : {}),
    minHeight: 46,
    height: Platform.OS === 'web' ? inputHeight : 'auto',
  },
  textareaContainer: {
    flex: 1,
    zIndex: 999999,
    minHeight: 46,
    height: Platform.OS === 'web' ? inputHeight : 'auto',
  },
  textarea: {
    backgroundColor: 'transparent',
    color: colors.text_color_primary,
    paddingVertical: 12,
    paddingRight: 12,
    paddingLeft: 16,
    fontSize: Platform.OS === 'macos' ? 14 : (isWorkMode ? 15 : 17),
    lineHeight: Platform.OS === 'macos' ? 21 : (isWorkMode ? 22.5 : 24),
    height: Platform.OS === 'web' ? inputHeight : 'auto',
    minHeight: 46,
    maxHeight: 240,
    ...(Platform.OS === 'web' && {
      border: 'none',
      resize: 'none',
      outlineStyle: 'none',
      WebkitAppearance: 'none',
    }),
    borderWidth: 0,
  },
  rightContainer: {
    marginLeft: 'auto',
    marginTop: 4,
    paddingRight: 4,
    height: 36,
    gap: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButton: {
    width: 34,
    height: 34,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    opacity: 1,
    backgroundColor: isDisabled ? 'transparent' : colors.solid_color_toast,
    ...(Platform.OS === 'web' && { transition: 'background-color 0.2s ease, opacity 0.2s ease' }),
  },
  actionButtonDisabled: {
    ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
    backgroundColor: colors.solid_color_tertiary,
  },
  actionButtonHovered: {
    backgroundColor: isDisabled ? colors.solid_color_tertiary : colors.solid_color_toast,
  },
  actionButtonLoadingWrapper: {
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
  },
});



export default function ChatInputFloating({
  placeholder = 'Type a message...',
  value = '',

  onActionPress,
  onSubmit,
  onChangeText,
  onFocus,
  onBlur,
  onPasteFiles,
  composerInputRef,
  commandSuggestions,
  goalBar,
  onCommandKeyPress,

  isInferencing = false,
  isLoading = false,
  isDisabled = false,
  isCancelable = false,
  withModels = false,
  selectedModelIds,
  selectedModels,
  onSetSelectedModels,
  selectedParameters,
  modelInputs,
  modelOutputs,
  conversationId,
  collectionId,
  hideVoiceMode = false,
  isWorkMode = false,
  workFollowUpMode = false,
  onSendNow,
  onQueue,
  onCancel,
  files,
}) {
  const { colors, theme } = useTheme();
  const [inputHeight, setInputHeight] = useState(46);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const dictateState = useDictateState();
  const isDictating = dictateState !== DICTATE_STATES.IDLE;
  const localInputRef = useRef(null);
  const inputRef = composerInputRef || localInputRef;
  const modelSelectorAnchorRef = useTooltipAnchor('model-selector');
  const voiceButtonAnchorRef = useTooltipAnchor('voice-button');
  const dictateAnchorRef = useTooltipAnchor('dictate-button');
  const themeStyles = getShadowThemeStyles(theme);
  const { isXS } = useResponsiveness();
  const enterToSend = useUIStore(state => state.enterToSend);
  const isMobileOrTablet = Device.deviceType === Device.DeviceType.PHONE
    || Device.deviceType === Device.DeviceType.TABLET;
  const submitOnEnter = shouldComposerSendOnEnter({
    platform: Platform.OS,
    isPad: Platform.isPad,
    isMobileOrTablet,
    enterToSend,
  });
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  
  // Calculate bubble bar padding for iOS Safari
  const bubbleBarPadding = useMemo(() => {
    if (!isXS) return 0;
    return getIOSSafariBubbleBarPadding(isInputFocused);
  }, [isXS, isInputFocused]);
  
  const styles = useMemo(() => createStyles(colors, theme, themeStyles, inputHeight, isDisabled, isXS, bubbleBarPadding, isWorkMode),
    [colors, theme, themeStyles, inputHeight, isDisabled, isXS, bubbleBarPadding, isWorkMode]);

  const isInferencingAndNotCancelable = isInferencing && !isCancelable;

  const handleKeyPress = (e) => {
    if (onCommandKeyPress?.(e)) return;
    if (Platform.OS === 'web' && isComposerEnterEvent(e)) {
      const shouldSendOnEnter = submitOnEnter;
      // Insert a newline with Shift/Command+Enter or when Enter-to-send is off.
      if (e.shiftKey || e.metaKey || !shouldSendOnEnter) {
        e.preventDefault();
        const cursorPosition = e.target.selectionStart;
        const newValue = value.slice(0, cursorPosition) + '\n' + value.slice(e.target.selectionEnd);
        onChangeText(newValue);
        // Set cursor position after the inserted newline and update height
        setTimeout(() => {
          e.target.selectionStart = e.target.selectionEnd = cursorPosition + 1;
          updateInputHeight(e.target);
        }, 0);
        return;
      }
      
      // Send only with plain Enter
      e.preventDefault();
      onSubmit?.();
    }
  };

  // Incomplete slash commands keep focus so the user can enter the objective.
  const handleSendPress = useCallback(() => {
    if (onActionPress?.() === false) return;
    if (Platform.OS !== 'web') {
      Keyboard.dismiss();
    }
  }, [onActionPress]);

  const handleSubmit = useCallback(() => {
    if (onSubmit?.() === false) return;
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') Keyboard.dismiss();
  }, [onSubmit]);
  
  const updateInputHeight = (element) => {
    if (Platform.OS === 'web') {
      if (!element) return;
      element.style.height = '46px';
      const newHeight = Math.min(element.scrollHeight, 240);
      element.style.height = `${newHeight}px`;
      setInputHeight(newHeight);
    }
  };

  const handleContentSizeChange = useCallback((measurement) => {
    const newHeight = getComposerContentHeight(measurement);
    if (newHeight !== null) setInputHeight(newHeight);
  }, []);

  // Wrap focus/blur handlers to track input focus for iOS Safari bubble bar padding
  const handleFocus = useCallback((e) => {
    setIsInputFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setIsInputFocused(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleDictateCancel = useCallback(() => {
    dictateModeService.cancel();
  }, []);

  const handleDictateSend = useCallback(() => {
    dictateModeService.stopRecording();
  }, []);

  const handleDictateTranscript = useCallback((text) => {
    const existing = value.trim();
    const newValue = existing ? `${existing} ${text}` : text;
    onChangeText(newValue);
    inputRef.current?.focus();
  }, [value, onChangeText]);

  return (
    <View style={styles.container}>
      <BrowserConnection isWorkMode={isWorkMode} />
      {goalBar}
      {!isDictating && commandSuggestions}
      <View style={[styles.inner, isDictating && { padding: 6, minHeight: 58 }]} id="chat-input-container">
        {isDictating && (
          <DictateRecordingOverlay
            dictateState={dictateState}
            onCancel={handleDictateCancel}
            onSend={handleDictateSend}
          />
        )}
        <View style={[styles.textareaContainer, isDictating && { display: 'none' }]}>
          <TextInput
            {...macComposerPasteProps(onPasteFiles)}
                {...(Platform.OS === 'macos' ? { enableFocusRing: false } : {})}
            dataSet={{ gabComposer: true }}
            ref={inputRef}
            multiline
            value={value}
            onChangeText={onChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            placeholderTextColor={themeStyles.placeholderColor}
            style={styles.textarea}
            onChange={Platform.OS === 'web' ? (e) => updateInputHeight(e.target) : undefined}
            onContentSizeChange={Platform.OS !== 'web' ? handleContentSizeChange : undefined}
            onKeyPress={Platform.OS === 'web' ? handleKeyPress : onCommandKeyPress}
            {...(Platform.OS !== 'web' ? { 
              submitBehavior: submitOnEnter ? 'submit' : 'newline',
              onSubmitEditing: submitOnEnter ? handleSubmit : undefined,
              ...(Platform.OS === 'macos' ? { submitKeyEvents: [] } : {})
            } : {})}
          />
        </View>

        <View style={[styles.rightContainer, isDictating && { display: 'none' }]}>
          {withModels && (
            <View ref={(node) => { modelSelectorAnchorRef.current = node; }}>
              <ChatInputModelsButton
                isDisabled={isInferencing}
                onChange={onSetSelectedModels}
                modelIds={selectedModelIds}
                shouldCloseOnSelect={true}
                maxModels={MAX_MODELS_SELECTABLE}
                inputs={modelInputs || (selectedParameters?.action === 'create-image' ? ['text'] : [])}
                outputs={modelOutputs || (selectedParameters?.action === 'create-image' ? ['image'] : [])}
                selectedModels={selectedModels}
                dropdownSide={conversationId && conversationId !== 'new' ? 'top' : 'bottom'}
              />
            </View>
          )}
          {isWorkMode ? (
            <WorkThinkingPowerButton
              conversationId={conversationId}
              collectionId={collectionId}
              compact
            />
          ) : (
            <FastModeButton
              isDisabled={isInferencing}
              conversationId={conversationId}
            />
          )}
          {workFollowUpMode ? (
            <WorkComposerActions
              hasContent={!!value?.trim() || !!files?.length}
              onQueue={onQueue}
              onSendNow={onSendNow}
              onStop={onCancel}
              isDisabled={isDisabled}
              isLoading={isLoading}
              onTranscript={handleDictateTranscript}
              dictateAnchorRef={dictateAnchorRef}
            />
          ) : !value?.trim() && !isInferencing ? (
            <>
              <View ref={(node) => { dictateAnchorRef.current = node; }}>
                <DictateModeButton
                  isDisabled={isInferencing}
                  onTranscript={handleDictateTranscript}
                />
              </View>
              {!hideVoiceMode && (
                <View ref={(node) => { voiceButtonAnchorRef.current = node; }}>
                  <VoiceModeButton
                    isDisabled={isInferencing}
                    conversationId={conversationId}
                  />
                </View>
              )}
            </>
          ) : (
            <Pressable 
              style={({ hovered }) => [
                styles.actionButton,
                hovered && styles.actionButtonHovered,
                isDisabled && styles.actionButtonDisabled,
              ]}
              {...(Platform.OS === 'web' ? { onPress: onActionPress } : { onPressIn: handleSendPress })}
              disabled={isDisabled}
            >
              {isLoading && (
                <View style={styles.actionButtonLoadingWrapper}>
                  <SpinningBorder />
                </View>
              )}
              {
                isInferencingAndNotCancelable ? null : (
                  <Icon 
                    name={isInferencing ? 'square' : 'arrow-up'}
                    solid={isInferencing}
                    size={isInferencing ? 14 : 16}
                    color={isDisabled ? colors.text_color_tertiary : colors.text_color_alt}
                  />
                )
              }
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
} 
